import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { OENCommand } from "app/order-entry/data/enums/oe-command.enum";
import { OENDisplayState } from "app/order-entry/data/enums/oe-display-state.enum";
import { OECommandEventModel } from "app/order-entry/data/models/oe-command-event.model";
import { OEModelGetterService } from "app/order-entry/services/model/oe-model-getter.service";
import { OEModelOperationsService } from "app/order-entry/services/model/oe-model-operations.service";
import { OEAPIChannelService } from "app/order-entry/services/oe-api-channel.service";
import { OECoordinatorService } from "app/order-entry/services/oe-coordinator.service";
import { Subject, Subscription } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ActiveUser, ActiveUserProfilePromiseService, CubiqWorker } from "shared/cubiq-services";
const logger = LoggerService.for(__dirname);

/**
 * Monitors all incoming LC events for changes that need to be reflected in active OE models
 */
@Injectable()
export class OEUpdateMonitorService implements OnInit, OnDestroy {
  private subscribedStrategyRefs: Set<string>;
  // private subscribedRootRefs: Map<string, string[]>;
  // private activeOrders: Map<string, OrderFillNowModel>;
  // private postRejectNotification: Map<string, CubiqTS.OrderLifeCycleEventImpl>;

  // private orderActionDialogSource = new Subject<BfxDialogData>();
  // // Observable stream for presenting dialogs
  // public dialogCtrl$ = this.orderActionDialogSource.asObservable();

  // private interfaceCmdEventSource = new Subject<OrderFillNowCmdPayload>();
  // /** Observable stream for all model changes */
  // public interfaceCmd$: Observable<OrderFillNowCmdPayload> =
  //   this.interfaceCmdEventSource.asObservable();

  // private activeUserCd: string;
  private subscriptions: Array<Subscription> = [];

  /** Definitions of all lifecycle events subscriptions required once we start Fill Now flow */
  private readonly eventSubscriptions = [
    {
      eventName: CubiqTS.OrderLifeCycleEventImpl.name,
      eventHandler: (event: CubiqTS.OrderLifeCycleEventImpl) =>
        this.processOrderLifecycleEvent(event),
    },
    {
      eventName: CubiqTS.BulkOrderLifeCycleEventImpl.name,
      eventHandler: (event: CubiqTS.BulkOrderLifeCycleEventImpl) =>
        this.processBulkOrderLifecycleEvent(event),
    },
    {
      eventName: CubiqTS.XferEventImpl.name,
      eventHandler: (event: CubiqTS.XferEventImpl) => this.processOrderXferEvent(event),
    },
  ];
  private activeUser: ActiveUser;

  constructor(
    private translate: TranslateService,
    protected cubiqWorker: CubiqWorker,
    private getter: OEModelGetterService,
    private modelOperations: OEModelOperationsService,
    activeUserService: ActiveUserProfilePromiseService,
    private coordinator: OECoordinatorService,
    private channelsService: OEAPIChannelService,
  ) {
    this.initializeSubscriptions();
    activeUserService.getActiveUserData().then((value: ActiveUser) => {
      this.activeUser = value;
    });
  }

  ngOnInit(): void {
    logger.out.info("oumon-s - Initialising OE Update Montoring");
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

  /**
   * Need to listen to all order lifecycle events
   */
  private initializeSubscriptions(): void {
    this.subscribedStrategyRefs = new Set();
  }

  processBulkOrderLifecycleEvent(event: CubiqTS.BulkOrderLifeCycleEventImpl): void {
    event.events.forEach((e) => this.processOrderLifecycleEvent(e));
  }

  /**
   *  Process a raw lifecycle event - it must be the latest for the order
   * @param event
   * @returns
   */
  processOrderLifecycleEvent(event: CubiqTS.OrderLifeCycleEventImpl): void {
    // if (this.subscribedOrders.size == 0 || !event.orderData?.orderId) {
    //   return;
    // }
    const order = event.orderData;
    if (order && this.subscribedStrategyRefs.has(order.strategyRef)) {
      const orderId = order.orderId.toString();
      logger.out.warn(`oumon-s - got subscribed LC event for order ${orderId}`);
      const eventType: CubiqTS.FxLifeCycleEventType = event.eventTypeId;

      const refId = order.clOrdID;
      const displayState = this.getter.getDisplayState(refId);
      if (displayState !== OENDisplayState.SHOW_MONITOR_EXECUTION) {
        logger.out.warn(
          `Ignoring LC event for ${refId} as displayState is ${OENDisplayState[displayState]}`,
        );
        return;
      }

      if (
        eventType === CubiqTS.FxLifeCycleEventType.TI_REQUIRED &&
        order.orderState !== CubiqTS.OrderState.ACCEPTANCE_NEEDED
      ) {
        // Only DI event we want to refresh from is after a modification
        logger.out.info(
          `em-s - ignoring DI event to update ticket for order ${order.orderRef} - orderState = ${
            CubiqTS.OrderState[order.orderState]
          }`,
        );
        return;
      }
      switch (eventType) {
        case CubiqTS.FxLifeCycleEventType.TI_REQUIRED:
          logger.out.info(
            `oumon-s - processing acceptance event to update ticket for order ${order.orderRef}`,
          );
          this.modelOperations.processExternalOrderUpdate(order);
          break;
        case CubiqTS.FxLifeCycleEventType.PENDING:
        case CubiqTS.FxLifeCycleEventType.ACTIVE:
        case CubiqTS.FxLifeCycleEventType.SI_REQUIRED:
        case CubiqTS.FxLifeCycleEventType.MODIFICATION_INFO:
        case CubiqTS.FxLifeCycleEventType.REPLACED:
          logger.out.info(
            `oumon-s - processing lifecycle event to update ticket for order ${order.orderRef} - eventType = ${CubiqTS.FxLifeCycleEventType[eventType]}`,
          );
          // These are the only LC messages that result in a visible difference in the view data for an order
          // Order has been unlocked so we need to refresh for monitoring

          // For strategy orders, any part of the strategy could have changed and we may not get that change
          // in an event (e.g. if the orders are in pending state) so need to do full refresh. However, currently this will
          // mess up the Display State and operation mode so needs to be revisited when Strategy support is re-enabled
          this.channelsService.refreshOrderStrategy(order.clOrdID, event.orderId);
          break;
        default:
          logger.out.info(
            `oumon-s - LC event for ${order.orderRef} does not require ticket update - eventType = ${CubiqTS.FxLifeCycleEventType[eventType]}`,
          );
          break;
      }
    }
  }

  /**
   *  Process a raw Xfer event
   * @param event
   * @returns
   */
  processOrderXferEvent(event: CubiqTS.XferEventImpl): void {
    const hasOrderData = Array.isArray(event.orderData) && event.orderData.length > 0;
    if (hasOrderData) {
      const orders = event.orderData;
      const isTransferred = orders[0].deskId.equals(event.targetGroupId);
      if (isTransferred) {
        // This is a successful transfer - now create a mpa of strategy-> set of transferred orders
        const refreshMap: Map<string, CubiqTS.OrderDataImpl[]> = new Map();

        orders.forEach((order) => {
          if (this.subscribedStrategyRefs.has(order.strategyRef)) {
            let orders = refreshMap.get(order.strategyRef);
            if (!orders) {
              orders = [];
              refreshMap.set(order.strategyRef, orders);
            }
            orders.push(order);
          }
        });

        // Update every order we know that has been transferred
        for (const [_strategyRef, orders] of refreshMap.entries()) {
          const refId = orders[0].clOrdID;
          if (this.activeUser && !this.activeUser.canSeeOrder(orders[0])) {
            // Entire strategy has gone out out of visibility
            logger.out.info(`oumon-s - strategy has left visibility - ref = ${refId}`);
            this.coordinator.cmdHideLostOrder(refId);
          } else {
            logger.out.info(`oumon-s - transferring visible strategy- ref = ${refId}`);
            orders.forEach((order) => this.modelOperations.processExternalTransfer(order));
          }
        }
      }
    }
  }

  /**
   * Handle a change in the lock state of a monitored order
   * @param event
   * @returns
   */
  private processLockLifecycleEvent(event: CubiqTS.LockLifeCycleEventImpl): void {}

  public subscribeStrategyEvents(strategyRef: string) {
    if (strategyRef && !this.subscribedStrategyRefs.has(strategyRef)) {
      this.subscribedStrategyRefs.add(strategyRef);
      if (this.subscriptions.length == 0) {
        const subs: Array<{ eventName: string; eventHandler: any }> = this.eventSubscriptions;
        this.subscriptions = subs.map((sub) => {
          return this.cubiqWorker.getEncodableObservable(sub.eventName).subscribe(sub.eventHandler);
        });
      }
    }
  }

  public unsubscribeStrategyEvents(strategyRef: string) {
    if (this.subscribedStrategyRefs.has(strategyRef)) {
      this.subscribedStrategyRefs.delete(strategyRef);
      if (this.subscribedStrategyRefs.size == 0) {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
      }
    }
  }
}
